import { motion, HTMLMotionProps, Variants } from 'framer-motion';
import { ReactNode } from 'react';
import { fadeIn } from '@/lib/motion/variants';
import { useReducedMotion } from '@/lib/motion/hooks/use-reduced-motion';

interface FadeInProps extends Omit<HTMLMotionProps<'div'>, 'children' | 'viewport'> {
  children: ReactNode;
  delay?: number;
  duration?: number;
  className?: string;
  /** Whether to animate when element enters viewport */
  viewport?: boolean;
  /** Custom variants to use instead of default fadeIn */
  variants?: Variants;
}

export function FadeIn({
  children,
  delay = 0,
  duration,
  className,
  viewport = false,
  variants = fadeIn,
  ...props
}: FadeInProps) {
  const reducedMotion = useReducedMotion();

  if (reducedMotion) {
    return <div className={className}>{children}</div>;
  }

  const animateProps = viewport
    ? {
        initial: 'hidden',
        whileInView: 'visible',
        viewport: { once: true, margin: '-50px' },
      }
    : {
        initial: 'hidden',
        animate: 'visible',
      };

  return (
    <motion.div
      variants={variants}
      {...animateProps}
      exit="exit"
      transition={{ delay, ...(duration && { duration }) }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  );
}
